'use client';
import { StickyTopMenu } from '@wwe-portal/ui/components/navigation/stick-menu';
import { RosterFlipCard } from '@wwe-portal/ui/features/roster';
import { AspectRatio } from '@wwe-portal/ui/components/aspect-ratio';
import cardBg from '@wwe-portal/ui/assets/images/roster/card-bg.svg';
import { useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import { sendTrendingSupersstarFlipButtonClickEvent } from '@wwe-portal/web/libs/analytics/superstars-v2';
import { SuperstarAvatarProps } from '@wwe-portal/entities/superstar.type';

export const useCardFlipController = (id: string) => {
  const [isFlipped, setIsFlipped] = useState<boolean>(false);

  const toggleFlip = () => {
    setIsFlipped((value) => !value);
    sendTrendingSupersstarFlipButtonClickEvent(id, 'superstar_details');
  };

  return {
    isFlipped,
    toggleFlip,
  };
};

export const SuperstarDetailsCard = ({
  data,
  id,
}: {
  data: SuperstarAvatarProps;
  id: string;
}) => {
  const { isFlipped, toggleFlip } = useCardFlipController(id);
  const textRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (
      textRef.current &&
      textRef.current.offsetWidth < textRef.current.scrollWidth
    ) {
      textRef.current.classList.add('animate-running-title');
    }
  }, [textRef]);

  return (
    <StickyTopMenu
      fluid={false}
      className="pb-0 pt-0 data-[sticky=true]:bg-transparent"
    >
      <div
        style={{
          background:
            'linear-gradient(180deg, rgba(16, 20, 47, 0.47) 62.02%, rgba(43, 50, 101, 0.47) 87.02%)',
        }}
      >
        <AspectRatio ratio={284 / 405} className="flex w-full flex-col">
          <div
            className="bg-back300 z-1 mx-2 mt-5 h-9"
            style={{
              filter: 'drop-shadow(0px 1px 3px rgba(0, 186, 255, 0.86))',
            }}
          >
            <div className="bg-plastic flex h-full items-center justify-center overflow-hidden font-bold uppercase">
              <span
                className="drop-shadow-fx w-full whitespace-nowrap px-2 text-center text-[#F5A200] hover:[animation-play-state:paused]"
                ref={textRef}
              >
                {data.name}
              </span>
            </div>
          </div>
          <div className="flex-1">
            <RosterFlipCard
              name={data.name}
              image={data.image ?? ''}
              stats={{
                classValue: {
                  class: data.class ?? '',
                  class_ID: data.class_ID ?? '',
                },
                tier: data.tier,
                playStyle: data.playStyle ?? '',
              }}
              videoLinks={data.videoLinks}
              isFlipped={isFlipped}
              handleFlipBack={toggleFlip}
              handleFlipFront={toggleFlip}
            />
          </div>
          <div className="absolute inset-0 top-10 -z-10">
            <Image
              src={cardBg}
              className="pointer-events-none object-fill object-bottom"
              fill
              alt=""
            />
          </div>
        </AspectRatio>
      </div>

      {isFlipped && (
        <div className="bg-back400 border-text700 mt-10 w-full rounded-lg border p-6 text-white shadow-lg">
          <p className="drop-shadow-text-2 text-md">{data.description}</p>
        </div>
      )}
    </StickyTopMenu>
  );
};
